import type { SipDirection, SipLogger, SipTimerPort } from './contracts';

const INBOUND_ICE_FALLBACK_MS = 900;
const INBOUND_ACCEPT_TIMEOUT_MS = 20_000;

type SessionListener = {
  name: string;
  handler: (event?: any) => void;
};

function eventMessage(event: any): string {
  return String(
    event?.cause
    || event?.data?.cause
    || event?.message?.reason_phrase
    || '',
  ).trim();
}

export function normalizeFreeSwitchRemoteAnswerSdp(
  sourceSdp: string,
  preferredMid = '0',
): string {
  const sdp = String(sourceSdp || '');
  if (!sdp || !/^m=audio\s/im.test(sdp)) return sdp;

  const newline = sdp.includes('\r\n') ? '\r\n' : '\n';
  const trailingNewline = /(?:\r\n|\n)$/.test(sdp);
  const body = sdp.replace(/(?:\r\n|\n)+$/, '');
  const lines = body.split(/\r\n|\n/);

  const firstMediaIndex = lines.findIndex(line => /^m=/i.test(line));
  if (firstMediaIndex < 0) return sdp;

  const audioIndexInitial = lines.findIndex(line => /^m=audio\s/i.test(line));
  if (audioIndexInitial < 0) return sdp;

  const nextMediaInitial = lines.findIndex(
    (line, index) => index > audioIndexInitial && /^m=/i.test(line),
  );
  const audioEndInitial = nextMediaInitial >= 0 ? nextMediaInitial : lines.length;
  const existingMidLine = lines
    .slice(audioIndexInitial, audioEndInitial)
    .find(line => /^a=mid:/i.test(line));
  const existingMid = String(existingMidLine || '')
    .replace(/^a=mid:/i, '')
    .trim();
  const mid = existingMid || String(preferredMid || '0').trim() || '0';
  const hasBundle = lines
    .slice(0, firstMediaIndex)
    .some(line => /^a=group:BUNDLE(?:\s|$)/i.test(line));

  if (!hasBundle) {
    lines.splice(firstMediaIndex, 0, `a=group:BUNDLE ${mid}`);
  }

  const audioIndex = lines.findIndex(line => /^m=audio\s/i.test(line));
  const nextMedia = lines.findIndex(
    (line, index) => index > audioIndex && /^m=/i.test(line),
  );
  const audioEnd = nextMedia >= 0 ? nextMedia : lines.length;
  const hasMid = lines
    .slice(audioIndex, audioEnd)
    .some(line => /^a=mid:/i.test(line));

  if (!hasMid) {
    lines.splice(audioIndex + 1, 0, `a=mid:${mid}`);
  }

  const normalized = lines.join(newline);
  return trailingNewline ? `${normalized}${newline}` : normalized;
}

export function normalizeSessionSdpEvent(event: any, session?: any): boolean {
  const payload = event?.data && typeof event.data === 'object'
    ? event.data
    : event;
  const originator = String(
    payload?.originator
    || event?.originator
    || '',
  ).toLowerCase();
  const type = String(
    payload?.type
    || event?.type
    || '',
  ).toLowerCase();

  if (originator && originator !== 'remote') return false;
  if (type && type !== 'offer' && type !== 'answer') return false;

  const originalSdp = String(
    payload?.sdp
    || event?.sdp
    || '',
  );
  if (!originalSdp || !/^m=audio\s/im.test(originalSdp)) return false;

  const localSdp = String(
    session?.connection?.localDescription?.sdp
    || '',
  );
  const localBundle = localSdp.match(/^a=group:BUNDLE\s+([^\r\n]+)/im);
  const preferredMid = String(
    localBundle?.[1]?.trim().split(/\s+/)[0]
    || '0',
  );
  const normalizedSdp = normalizeFreeSwitchRemoteAnswerSdp(
    originalSdp,
    preferredMid,
  );

  if (normalizedSdp === originalSdp) return false;

  if (payload && typeof payload === 'object') {
    payload.sdp = normalizedSdp;
  }

  if (
    event
    && typeof event === 'object'
    && event !== payload
    && 'sdp' in event
  ) {
    event.sdp = normalizedSdp;
  }

  return true;
}

export function isRelayIceCandidate(candidate: unknown): boolean {
  const value = typeof candidate === 'string'
    ? candidate
    : String(
        (candidate as { candidate?: string } | null)?.candidate
        || '',
      );

  return /\styp\s+relay(?:\s|$)/i.test(value);
}

export function createOutgoingRelayIceHandler(
  logger: SipLogger = console,
): (event: any) => void {
  let released = false;

  return (event: any) => {
    if (released) return;
    if (!isRelayIceCandidate(event?.candidate)) return;
    if (typeof event?.ready !== 'function') return;

    released = true;

    try {
      event.ready();
      logger.info('[telephony] v14 first TURN relay candidate released');
    } catch (error) {
      released = false;
      logger.warn('[telephony] v14 ICE release failed', error);
    }
  };
}

export class SessionWebRtcCompatibility {
  private readonly session: any;
  private readonly direction: SipDirection;
  private readonly timers: SipTimerPort;
  private readonly logger: SipLogger;
  private readonly isCurrent: () => boolean;
  private readonly boundListeners: SessionListener[] = [];
  private answerListeners: SessionListener[] = [];
  private inboundIceFallbackTimer: number | null = null;
  private answerTimeout: number | null = null;
  private answerPromise: Promise<void> | null = null;
  private cancelAnswerWait: (() => void) | null = null;
  private bound = false;
  private disposed = false;
  private inboundIceReleased = false;

  constructor(args: {
    session: any;
    direction: SipDirection;
    timers: SipTimerPort;
    logger: SipLogger;
    isCurrent: () => boolean;
  }) {
    this.session = args.session;
    this.direction = args.direction;
    this.timers = args.timers;
    this.logger = args.logger;
    this.isCurrent = args.isCurrent;
  }

  bind(): void {
    if (this.bound || this.disposed) return;
    this.bound = true;

    this.addBoundListener('sdp', (event: any) => {
      if (this.disposed) return;
      if (!normalizeSessionSdpEvent(event, this.session)) return;

      const payload = event?.data && typeof event.data === 'object'
        ? event.data
        : event;
      const type = String(payload?.type || event?.type || '').toLowerCase();
      this.logger.info(
        `[telephony] FreeSWITCH remote SDP ${type || 'description'} normalized: restored BUNDLE/MID`,
      );
    });

    if (this.direction === 'INBOUND') {
      this.addBoundListener('icecandidate', event => this.releaseIncomingIce(event));
    }
  }

  waitForInboundAccepted(
    timeoutMs = INBOUND_ACCEPT_TIMEOUT_MS,
  ): Promise<void> {
    if (this.disposed) {
      return Promise.reject(new Error('Входящий звонок завершён до соединения.'));
    }
    if (this.answerPromise) return this.answerPromise;

    this.answerPromise = new Promise((resolve, reject) => {
      let settled = false;

      const cleanup = () => {
        this.removeAnswerListeners();
        if (this.answerTimeout !== null) {
          this.timers.clearTimeout(this.answerTimeout);
          this.answerTimeout = null;
        }
      };
      const finish = (error?: Error, allowDisposed = false) => {
        if (settled || (this.disposed && !allowDisposed)) return;
        settled = true;
        cleanup();
        this.cancelAnswerWait = null;

        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };
      const onAccepted = () => finish();
      const onConfirmed = () => finish();
      const onFailed = (event: any) => finish(new Error(
        eventMessage(event) || 'Не удалось принять входящий звонок.',
      ));
      const onEnded = (event: any) => finish(new Error(
        eventMessage(event) || 'Входящий звонок завершён до соединения.',
      ));

      this.cancelAnswerWait = () => finish(
        new Error('Входящий звонок завершён до соединения.'),
        true,
      );

      this.addAnswerListener('accepted', onAccepted);
      this.addAnswerListener('confirmed', onConfirmed);
      this.addAnswerListener('failed', onFailed);
      this.addAnswerListener('ended', onEnded);

      this.answerTimeout = this.timers.setTimeout(() => {
        if (this.disposed || settled) return;

        try {
          this.session?.terminate?.({
            status_code: 480,
            reason_phrase: 'WebRTC answer timeout',
          });
        } catch {
          // Публичным результатом остаётся ошибка таймаута ниже.
        }

        finish(new Error(
          'Браузер не завершил WebRTC-соединение за 20 секунд.',
        ));
      }, timeoutMs);
    });

    return this.answerPromise;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.cancelAnswerWait?.();
    this.removeListeners(this.boundListeners);
    this.removeAnswerListeners();
    this.clearInboundIceFallback();

    if (this.answerTimeout !== null) {
      this.timers.clearTimeout(this.answerTimeout);
      this.answerTimeout = null;
    }
  }

  private addBoundListener(name: string, handler: (event?: any) => void): void {
    this.session?.on?.(name, handler);
    this.boundListeners.push({ name, handler });
  }

  private addAnswerListener(name: string, handler: (event?: any) => void): void {
    this.session?.on?.(name, handler);
    this.answerListeners.push({ name, handler });
  }

  private removeAnswerListeners(): void {
    this.removeListeners(this.answerListeners);
    this.answerListeners = [];
  }

  private removeListeners(listeners: SessionListener[]): void {
    for (const { name, handler } of listeners.splice(0)) {
      try {
        this.session?.removeListener?.(name, handler);
      } catch {
        // Очистка обработчиков выполняется по возможности для совместимости с JsSIP.
      }
    }
  }

  private clearInboundIceFallback(): void {
    if (this.inboundIceFallbackTimer === null) return;
    this.timers.clearTimeout(this.inboundIceFallbackTimer);
    this.inboundIceFallbackTimer = null;
  }

  private releaseIncomingIce(event: any): void {
    if (this.disposed || !this.isCurrent()) return;
    if (this.inboundIceReleased) return;
    if (typeof event?.ready !== 'function') return;

    const ready = () => event.ready();
    const release = (mode: 'relay' | 'fallback') => {
      if (this.disposed || !this.isCurrent() || this.inboundIceReleased) return;

      this.inboundIceReleased = true;
      this.clearInboundIceFallback();

      try {
        ready();
        this.logger.info(
          mode === 'relay'
            ? '[telephony] v15.7.9 inbound TURN relay ICE released'
            : '[telephony] v15.7.9 inbound ICE fallback released after relay wait',
        );
      } catch (error) {
        this.inboundIceReleased = false;
        this.logger.warn('[telephony] v15.7.9 inbound ICE release failed', error);
      }
    };

    if (isRelayIceCandidate(event?.candidate)) {
      release('relay');
      return;
    }

    if (this.inboundIceFallbackTimer === null) {
      this.inboundIceFallbackTimer = this.timers.setTimeout(() => {
        this.inboundIceFallbackTimer = null;
        release('fallback');
      }, INBOUND_ICE_FALLBACK_MS);
    }
  }
}
